import { Checkbox, FormControl, FormHelperText, Stack, Text, Textarea } from '@chakra-ui/react'
import { getMeetingRecordCheckboxValues, useOnCheckboxChange } from 'BrokerTsLib/src/hooks'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { getValueByPath } from 'BrokerTsLib/src/Utils/formWarnings'
import React from 'react'
import { FormLabel, type FormLabelProps } from '..'


type Props = {
    name: string
    label: string
    tooltip?: string
    isRequired?: boolean
    variant?: 'fullWidth' | undefined
    textareaPlaceholder?: string
    helperText?: string
    checkboxTexts: { [key: string]: string; }
    isDisabled?: boolean
    textareaVariant?: 'flushed' | 'outline' | 'filled' | 'unstyled'
    hintType?: FormLabelProps['hintType']
}

/**
 * Renders a FormControl containing a list of predefined checkboxes driven by
 * useMeetingRecordCheckbox and the corresponding textarea registered in the form.
 *
 * This component must be rendered inside a FormProvider so useFormContext() is available.
 */
export const MeetingRecordCheckboxesFormControl: React.FC<Props> = ({
    name,
    label,
    tooltip,
    isRequired = false,
    variant,
    textareaPlaceholder,
    helperText,
    checkboxTexts,
    isDisabled,
    textareaVariant,
    hintType
}) => {
    const { register, formState, watch } = useFormContext()
    const errors = (formState?.errors as any) ?? {}

    const onCheckboxChange = useOnCheckboxChange(name)

    const value = watch(name)
    const checkboxValues = getMeetingRecordCheckboxValues(checkboxTexts, value)

    const error = getValueByPath(errors, name)
    
    return (
        <FormControl variant={variant} isInvalid={!!errors[name]?.message} isRequired={isRequired}>
            <FormLabel htmlFor={name} hint={tooltip} hintType={hintType}>{label}</FormLabel>

            <Stack gap={2}>
                {checkboxValues?.map(([key, text, isChecked]: [string, string, boolean]) => (
                    <Checkbox
                        key={key}
                        value={key}
                        isChecked={isChecked}
                        onChange={(e) => onCheckboxChange(text, e.target.checked)}
                        isDisabled={isDisabled}
                    >
                        <Text lineHeight="5">{text}</Text>
                    </Checkbox>
                ))}
            </Stack>

            <Textarea
                mt={4}
                w="full"
                placeholder={textareaPlaceholder ?? label}
                variant={textareaVariant}
                {...register(name)}
            />

            {helperText && <FormHelperText>{helperText}</FormHelperText>}

            {error && (
                <FormHelperText color="red.500">
                    {error.message as string}
                </FormHelperText>
            )}
        </FormControl>
    )
}